<script lang="ts">
	import { get } from 'svelte/store';
	import { format } from 'svelte-i18n';
	//Design system components
	import { Button, toast } from '@Shelly/shelly-design-system';
	//Helpers
	import { getTrvComponentByDevice } from 'src/helpers/virtual_helpers/blutrv_helpers';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { saveVCSettings } from 'src/helpers/virtual_helpers/virtual_components_settings_helpers';
	//Components
	import BthomeDeviceSettings from '../Settings/BthomeDeviceSettings.svelte';
	import VcModalRemove from '../Settings/VCModalRemove.svelte';
	//Types
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { TRVComponentInterface } from 'src/types/TRVComponent';
	import type { VirtualComponentInterface } from 'src/types/VirtualComponents';
	import BluTrvStartAssociations from './BluTrvStartAssociations.svelte';
	import BluTrvAttachedSensorsContainer from './BluTrvAttachedSensorsContainer.svelte';

	export let device: Device;

	const _device = device;

	let tempSensors: string[] = [];
	let dwSensors: string[] = [];
	let waitForResponse = false;
	let waitForSaveResponse = false;
	let showAssociationsModal = false;
	let inclusionStep = 1;

	let editSensorModal = false;
	let removeSensorModal = false;
	let selectedSensor: VirtualComponentInterface = null;

	const addlistenerNewTRVSensors = (device: Device) => {
		const trvComponent: TRVComponentInterface = getTrvComponentByDevice(device);
		const _tempSensors: string[] = trvComponent?.config?.temp_sensors || [];
		const _dwSensors: string[] = trvComponent?.config?.dw_sensors || [];
		if (inclusionStep == 2) {
			const initSensors = [...tempSensors, ...dwSensors];
			const sensors = [..._tempSensors, ..._dwSensors];
			if (sensors?.length > initSensors?.length) {
				showAssociationsModal = false;
				inclusionStep = 1;
			}
		}
		tempSensors = _tempSensors;
		dwSensors = _dwSensors;
	};

	const saveBtHomeSettings = async (e: CustomEvent) => {
		waitForSaveResponse = true;
		const { settings, type } = e.detail;
		const res: SettingRequest = await saveVCSettings(device, type, settings);
		if (res?.isok) editSensorModal = false;
		waitForSaveResponse = false;
	};

	const removeBtHomeSensor = async (e: CustomEvent) => {
		waitForSaveResponse = true;
		const { id } = e.detail.config;
		const res: SettingRequest = await gen2Request(
			device,
			'bthome.deletedevice',
			{
				id,
			},
			true,
		);
		if (res?.isok) {
			toast.open({
				type: 'success',
				title: get(format)(`type_del_scc_toast`, {
					values: { this_type: get(format)(`process_delete_device`) },
				}),
			});
			removeSensorModal = false;
		} else {
			onSettingsResponse(res, device);
		}
		waitForSaveResponse = false;
	};

	$: addlistenerNewTRVSensors(device);

	const changeSensor = (e) => {
		selectedSensor = e.detail.selectedSensor;
		if (e.detail.action === 'edit') {
			editSensorModal = true;
		} else if (e.detail.action === 'delete') {
			removeSensorModal = true;
		}
	};
</script>

<BluTrvAttachedSensorsContainer
	type="temp"
	{device}
	sensors={tempSensors}
	on:change_sensor={changeSensor}
/>

<BluTrvAttachedSensorsContainer type="dw" {device} sensors={dwSensors} on:change_sensor={changeSensor} />

<Button
	size="m"
	width="full"
	on:click={() => (showAssociationsModal = true)}
	loading={waitForResponse}
	disabled={waitForResponse}
>
	{$format('text_blu_trv_add_new_sensor', {
		default: 'Add New TRV Sensor',
	})}
</Button>

{#if showAssociationsModal}
	<BluTrvStartAssociations
		device={_device}
		bind:showModal={showAssociationsModal}
		bind:inclusionStep
	/>
{/if}

{#if editSensorModal}
	<BthomeDeviceSettings
		component={selectedSensor}
		waitForResponse={waitForSaveResponse}
		bind:showModal={editSensorModal}
		on:edit={saveBtHomeSettings}
	/>
{:else if removeSensorModal}
	<VcModalRemove
		component={selectedSensor}
		waitForResponse={waitForSaveResponse}
		bind:showModal={removeSensorModal}
		on:remove={removeBtHomeSensor}
	/>
{/if}
